// @ts-strict-ignore
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';

import {getEventValueAndLabel} from 'common/report/util/events';
import {findKeyByValue} from 'common/util/typescript';
import {Model, ModelStatus} from 'common/util/model-resource/model-resource';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {
  BehaviorMetric,
  BehaviorShowClause,
  MetricBehavior,
  MetricMeasurementProperty,
  ShowClause as ShowClauseType,
} from 'common/report/insights/util/types';
import {
  ConversionWindowUnit,
  FunnelOrder,
  FunnelReentryModeType,
  InsightsResourceType,
  MetricType,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  SubBehavior,
  TopLevelMetricType,
} from 'common/types/reports/bookmark';
import {
  FiltersOperator,
  MetricEntry,
  MetricEntryBehavior,
  QuerySavedMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {DATASET_MIXPANEL} from 'common/report/constants';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {BEHAVIOR_METRIC_TYPES, QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from 'common/report/insights/models/constants';
import {isProperty, Property} from 'common/query-builder/query-block/properties-menu/types';
import {ShowClause} from 'common/report/insights/models/show-clause';
import {isBehaviorShowClause, isFormulaShowClause, isSavedMetricShowClause} from 'common/report/insights/models/utils';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Metric} from 'app_api/projects/metrics/types';

export function transformFunnelEntryToMetricBehavior(behavior: MetricEntryBehavior): MetricBehavior {
  const renamed = !isNil(behavior.id) ? null : behavior.renamed;

  return {
    id: behavior.id,
    type: MetricType.Funnel,
    ...(behavior.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
    ...(behavior.name ? {name: behavior.name} : {}),
    ...(renamed ? {renamed} : {}),
    resourceType: InsightsResourceType.Events,
    dataGroupId: behavior.dataGroupId,
    behaviors: behavior.behaviors.map((subBehavior) => {
      if (`behaviors` in subBehavior) {
        const newCompareBehaviors = subBehavior.behaviors.map((compareBehavior) => {
          const metricBehavior: SubBehavior = {
            id: compareBehavior.id ?? undefined,
            type: compareBehavior.type as SubBehavior[`type`],
            name: compareBehavior.name || undefined,
            renamed: compareBehavior.renamed,
            filters: compareBehavior.filters?.map((filter) => FilterClause.fromPropertyFilter(filter).serialize()),
            filtersDeterminer: compareBehavior.filtersDeterminer ?? `all`,
          };
          return metricBehavior;
        });
        return {
          type: subBehavior.type as SubBehavior[`type`],
          funnelOrder: subBehavior.funnelOrder,
          behaviors: newCompareBehaviors,
        } as SubBehavior;
      }
      return {
        id: subBehavior.id ?? undefined,
        type: subBehavior.type as SubBehavior[`type`],
        name: subBehavior.name || undefined,
        renamed: subBehavior.renamed,
        filters: subBehavior.filters?.map((filter) => FilterClause.fromPropertyFilter(filter).serialize()),
        filtersDeterminer: subBehavior.filtersDeterminer ?? `all`,
        funnelOrder: subBehavior.funnelOrder ? subBehavior.funnelOrder : FunnelOrder.Loose,
      };
    }),
    conversionWindowDuration: behavior.conversionWindowDuration,
    funnelReentryMode: behavior.funnelReentryMode || FunnelReentryModeType.Basic,
    conversionWindowUnit: behavior.conversionWindowUnit,
    funnelOrder: behavior.funnelOrder,
    exclusions: behavior.exclusions || [],
    aggregateBy: behavior.aggregateBy,
  };
}

function transformRetentionEntryBehaviorToMetricBehavior(
  behavior: MetricEntryBehavior,
  metricType = behavior.type,
): MetricBehavior {
  const bornBehavior = behavior.behaviors[0];
  const subBehaviors = [
    {
      id: bornBehavior?.id ?? undefined,
      type: bornBehavior?.type as SubBehavior[`type`],
      name: bornBehavior?.name,
      renamed: bornBehavior?.renamed,
      filters: bornBehavior?.filters?.map((filter) => FilterClause.fromPropertyFilter(filter).serialize()),
      filtersDeterminer: bornBehavior?.filtersDeterminer ?? `all`,
    },
  ];
  if (metricType === MetricType.Retention) {
    const retainedBehavior = behavior.behaviors[1];
    subBehaviors.push({
      id: retainedBehavior?.id ?? undefined,
      type: (retainedBehavior?.type as SubBehavior[`type`]) ?? MetricType.Event,
      name: retainedBehavior?.name,
      renamed: retainedBehavior?.renamed,
      filters: retainedBehavior?.filters?.map((filter) => FilterClause.fromPropertyFilter(filter).serialize()) ?? [],
      filtersDeterminer: retainedBehavior?.filtersDeterminer ?? `all`,
    });
  }
  const metricTypeChanged = !isEqual(metricType, behavior.type);

  return {
    ...(!metricTypeChanged ? {id: behavior.id} : {}),
    type: metricType,
    resourceType: InsightsResourceType.Events,
    dataGroupId: behavior.dataGroupId,
    dataset: DATASET_MIXPANEL,
    ...(behavior.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
    ...(behavior.renamed ? {renamed: behavior.renamed} : {}),
    ...(behavior.name ? {name: behavior.name} : {}),
    behaviors: subBehaviors,
    retentionType: metricType === MetricType.Retention ? `birth` : `addiction`,
    retentionAlignmentType: behavior.retentionAlignmentType,
    retentionUnit: behavior.retentionUnit,
    retentionUnboundedMode: behavior.retentionUnboundedMode,
    retentionCustomBucketSizes: behavior.retentionCustomBucketSizes,
    disableCohortize: behavior.disableCohortize,
    filters: [],
  };
}

function getMetricModel(clause: MetricEntry | BehaviorShowClause, metrics: MetricsResource): Model<Metric> | null {
  if (clause.type !== TopLevelMetricType.Metric) {
    throw new Error(`Entry type mismatch. Expected: ${TopLevelMetricType.Metric}. Received: ${clause.type}.`);
  }
  if (clause?.id) {
    if (metrics) {
      return metrics.getOne(clause.id) as Model<Metric>;
    }
    return {status: ModelStatus.Loading};
  }
  return null;
}

export function getSavedMetricName(entry: QuerySavedMetricEntry, metrics: MetricsResource): string {
  const model = getMetricModel(entry.event, metrics);
  return model?.value?.name;
}

/**
 * Get top level show.behavior definition
 */
export function getMetricEntryBehavior(behavior: BehaviorMetric[`behavior`]): MetricEntryBehavior {
  let reportSpecificFields: Partial<MetricEntryBehavior> = {};
  const commonFields = {
    // ...convertBehaviorToMetricEntryBehavior(behavior),
    // Common behavior fields
    id: behavior.id,
    type: behavior.type,
    dataGroupId: behavior.dataGroupId,
    // @todo(metrics) this is a presentation level concern, it shouldn't be part of serializing bookmark fields
    name: getMetricEntryBehaviorName(behavior),
    renamed: behavior.renamed,
    behaviors: getMetricEntryBehaviors(behavior), // for inline-custom-event, inline-funnel, inline-retention
    filters: [],
    filtersDeterminer: behavior.filters?.length ? behavior.filtersDeterminer || `all` : `all`,
    ...(behavior.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
    ...(behavior.customBucket ? {customBucket: behavior.customBucket} : {}),
  };

  /** this ensures filters on cohort and profile metrics are passed down  */
  if (behavior.filters) {
    commonFields.filters = behavior.filters.map((filter) => new FilterClause(filter).toPropertyFilter());
  }

  // We need resourceType: `events` for events or API bombs.
  if (behavior.type === MetricType.Event) {
    (commonFields as any).resourceType = InsightsResourceType.Events;
  } else if (behavior.type === MetricType.Funnel) {
    /** funnel specific fields */
    reportSpecificFields = {
      funnelOrder: behavior.funnelOrder || FunnelOrder.Loose,
      exclusions: behavior.exclusions || [],
      aggregateBy: behavior.aggregateBy || [],
      conversionWindowDuration: behavior.conversionWindowDuration || 7,
      funnelReentryMode: behavior.funnelReentryMode || FunnelReentryModeType.Basic,
      conversionWindowUnit: behavior.conversionWindowUnit || ConversionWindowUnit.Day,
    };
  } else if (behavior.type === MetricType.Retention || behavior.type === MetricType.RetentionFrequency) {
    /** retention specific fields */
    reportSpecificFields = {
      retentionType: behavior.type === MetricType.Retention ? `birth` : `addiction`,
      retentionAlignmentType: behavior.retentionAlignmentType || RetentionAlignmentType.Birth,
      retentionUnit: behavior.retentionUnit || TimeUnit.Day,
      retentionUnboundedMode: behavior.retentionUnboundedMode,
      retentionCustomBucketSizes: behavior.retentionCustomBucketSizes || [],
      disableCohortize: behavior.disableCohortize,
    };
  } else if (behavior.type === MetricType.People) {
    /** profile metric fields */
    reportSpecificFields = {
      name: `$all_people`,
    };
  } else if (behavior.type === MetricType.Cohort) {
    /** cohort metric fields */
    if (behavior.id) {
      reportSpecificFields = {
        name: null,
        id: behavior.id,
      };
    } else {
      reportSpecificFields = {
        name: behavior.name,
        raw_cohort: behavior.raw_cohort,
      };
    }
  }

  return {
    ...commonFields,
    ...reportSpecificFields,
  };
}

function getMetricEntryBehaviorName(behavior: BehaviorMetric[`behavior`]): string {
  const {type, name} = behavior;
  if (name) {
    return name;
  }
  if (type === MetricType.Funnel) {
    const behaviors = behavior.behaviors || [];
    const hasEventComparison = behaviors.some((beh) => !!beh.behaviors);
    if (hasEventComparison) {
      return `Step 1 through Step ${behaviors.length}`;
    }
    if (behaviors.length < 2) {
      return null;
    }
    const firstBehavior = behaviors[0];
    const lastBehavior = behaviors[behaviors.length - 1];
    if (firstBehavior.name && lastBehavior.name) {
      const firstStep = getEventValueAndLabel({
        name: firstBehavior.type === MetricType.CustomEvent ? null : firstBehavior.name,
        custom: firstBehavior.type === MetricType.CustomEvent,
        id: firstBehavior.id,
      });
      const lastStep = getEventValueAndLabel({
        name: lastBehavior.type === MetricType.CustomEvent ? null : lastBehavior.name,
        custom: lastBehavior.type === MetricType.CustomEvent,
        id: lastBehavior.id,
      });
      return `${firstStep.label} through ${lastStep.label}`;
    }
  }
  if (type === MetricType.Retention) {
    const behaviors = behavior.behaviors || [];
    const firstBehavior = behaviors[0];
    const lastBehavior = behaviors[behaviors.length - 1];
    if (behaviors.length < 2 || !firstBehavior?.name || !lastBehavior?.name) {
      return null;
    }
    if (firstBehavior.name && lastBehavior.name) {
      const bornEvent = getEventValueAndLabel({
        name: firstBehavior.type === MetricType.CustomEvent ? null : firstBehavior.name,
        custom: firstBehavior.type === MetricType.CustomEvent,
        id: firstBehavior.id,
      });
      const event = getEventValueAndLabel({
        name: lastBehavior.type === MetricType.CustomEvent ? null : lastBehavior.name,
        custom: lastBehavior.type === MetricType.CustomEvent,
        id: lastBehavior.id,
      });
      return `${bornEvent.label} and then ${event.label}`;
    }
  }
  if (type === MetricType.RetentionFrequency) {
    if (behavior.behaviors.length !== 1) {
      return null;
    }
    if (behavior.behaviors[0].name) {
      const event = getEventValueAndLabel({
        name: behavior.behaviors[0].type === MetricType.CustomEvent ? null : behavior.behaviors[0].name,
        custom: behavior.behaviors[0].type === MetricType.CustomEvent,
        id: behavior.behaviors[0].id,
      });
      return `Frequency of ${event.label}`;
    }
  }
  return ``;
}

export function getMetricName({
  showClause,
  metricsResource,
  regenerate = false,
}: {
  showClause: ShowClauseType;
  metricsResource: MetricsResource;
  regenerate?: boolean;
}): string {
  if ((!regenerate || showClause[`userNamed`]) && showClause.name) {
    return showClause.name;
  }
  if (isSavedMetricShowClause(showClause) || isFormulaShowClause(showClause)) {
    return (showClause.id && metricsResource.getOne(showClause.id)?.value?.name) || ``;
  }
  if (isBehaviorShowClause(showClause) && showClause.behavior) {
    const {id, name, renamed, type} = showClause.behavior;
    if (renamed) {
      return renamed;
    }
    if (type === MetricType.People) {
      return `All User Profiles`;
    }
    if ([MetricType.Event, MetricType.CustomEvent].includes(type)) {
      const {label} = getEventValueAndLabel({
        name: type === MetricType.CustomEvent ? null : name,
        custom: type === MetricType.CustomEvent,
        id,
      });
      return label ? `${label}` : ``;
    }
    return getMetricEntryBehaviorName(showClause.behavior);
  }
  return ``;
}

function convertBehaviorToMetricEntryBehavior(behavior: SubBehavior): MetricEntryBehavior {
  const metricEntryBehavior: MetricEntryBehavior = {
    id: behavior.id,
    type: behavior.type,
    name: behavior.name,
    renamed: behavior.renamed,
    filters: behavior.filters?.map((filter) => new FilterClause(filter).toPropertyFilter()),
    filtersOperator:
      behavior.filters?.length && behavior.filtersDeterminer === `any` ? FiltersOperator.Or : FiltersOperator.And,
    filtersDeterminer: behavior.filters?.length ? behavior.filtersDeterminer || `all` : `all`,
    funnelOrder: behavior.funnelOrder,
  };

  return omitBy(metricEntryBehavior, (value) => value === undefined) as MetricEntryBehavior;
}

function getMetricEntryBehaviors(behavior: BehaviorMetric[`behavior`]): MetricEntryBehavior[] {
  const subBehaviors = behavior.behaviors || [];

  if (behavior.type === MetricType.Simple) {
    return subBehaviors.map((beh) => convertBehaviorToMetricEntryBehavior(beh));
  } else if (behavior.type === MetricType.Funnel) {
    return subBehaviors.map((beh) => {
      if (beh.behaviors) {
        const compareBehaviors = beh.behaviors;
        return {
          // @todo(metrics) Should the container for event comparison behaviors
          // have type "funnel"?
          type: MetricType.Funnel,
          id: null,
          name: null,
          funnelOrder: beh.funnelOrder,
          behaviors: compareBehaviors.map((b) => convertBehaviorToMetricEntryBehavior(b)),
        };
      }
      return convertBehaviorToMetricEntryBehavior(beh);
    });
  }
  if (behavior.type === MetricType.Retention || behavior.type === MetricType.RetentionFrequency) {
    return subBehaviors.map((beh) => ({
      ...(beh?.name?.startsWith(`$custom_event:`) ? {id: parseInt(beh.name.split(`:`)[1], 10)} : {}),
      type: beh?.name?.startsWith(`$custom_event:`) ? MetricType.CustomEvent : MetricType.Event,
      name: beh.name,
      renamed: beh.renamed,
      filters: beh.filters?.length
        ? beh.filters.map((filterClause) => new FilterClause(filterClause).toPropertyFilter())
        : [],
      filtersDeterminer: beh.filters?.length ? beh.filtersDeterminer || `all` : `all`,
    }));
  }

  return [];
}

export function convertBookmarkPropertyToPropertySelection(
  bookmarkProperty: MetricMeasurementProperty,
  behavior: MetricEntryBehavior | MetricBehavior, // TODO: Should this be just MetricBehavior?
) {
  if (bookmarkProperty) {
    const {
      listItemGroup = null,
      customProperty = null,
      customPropertyId = null,
      dataGroupId = null,
      tempDataGroupId = null,
      defaultType = null,
      joinPropertyType = null,
      propertyObjectKey = null,
      scdEvent = null,
      scdOffset = null,
      name,
      type,
      unit = null,
      // label = null,
      resourceType: insightsResourceType,
      behavior: propertyBehavior,
    } = bookmarkProperty;
    const resourceType = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, insightsResourceType);
    return {
      resourceType,
      dataGroupId: dataGroupId ?? behavior.dataGroupId,
      tempDataGroupId: tempDataGroupId ?? null,
      propertyDefaultType: defaultType,
      type,
      unit,
      value: name,
      // label: label ? label : name,
      label: typeof name === `string` ? name : name[0],
      ...(propertyBehavior ? {behavior: propertyBehavior} : {}),
      ...(propertyObjectKey ? {propertyObjectKey} : {}),
      ...(customProperty ? {customProperty} : {}),
      ...(customPropertyId ? {customPropertyId} : {}),
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(listItemGroup ? {listItemGroup} : {}),
      ...(scdEvent ? {scdEvent} : {}),
      ...(scdOffset ? {scdOffset} : {}),
    };
  }
  return null;
}

export function createRetentionMetricShowClause(
  metricType: MetricType,
  entry: MetricEntry,
  attributionSelection: number | BehaviorShowClause[`measurement`][`actionMode`],
  forceExpand: boolean = false,
) {
  const metricTypeChanged = !isEqual(metricType, entry.behavior.type);
  const behavior =
    !forceExpand && !metricTypeChanged && isSavedBehavior(entry) && !entry.hasUnsavedChanges
      ? {
          id: entry.behavior.id,
          type: entry.behavior.type,
          name: entry.behavior.name,
        }
      : transformRetentionEntryBehaviorToMetricBehavior(entry.behavior, metricType);
  const entryAttributionSelection = attributionSelection || entry.measurement.attributionSelection;
  const retentionSegmentationEvent = entryAttributionSelection
    ? (entryAttributionSelection as string)
    : entry.behavior.behaviors?.[1]?.name;
  const measurement: BehaviorShowClause[`measurement`] = {
    math: entry.measurement.aggregationOperator || RETENTION_MATH_TYPES.UNIQUE,
    perUserAggregation: null,
    rolling: null,
    cumulative: entry.measurement.cumulative || false,
    retentionCumulative: entry.measurement.retentionCumulative || false,
    property: entry.measurement.property
      ? convertPropertySelectiontoBookmarkProperty(entry.measurement.property)
      : null,
    retentionBucketIndex: entry.measurement.retentionBucketIndex,
    retentionSegmentationEvent: metricType === MetricType.Retention ? retentionSegmentationEvent : null,
    segmentMethod: entry.measurement.countUsersOnceType,
    multiAttribution: entry.measurement.multiAttribution,
    ...(entry.measurement.dataGroupId ? {dataGroupId: entry.measurement.dataGroupId} : {}),
  };
  return ShowClause.createShowClause({
    ...(entry._idx ? {_idx: entry._idx} : {}),
    ...(entry.id ? {id: entry.id} : {}),
    ...(entry.name ? {name: entry.name} : {}),
    isHidden: entry.isHidden ?? false,
    isExpanded: entry.isExpanded ?? false,
    display: entry.display,
    goals: entry.goals,
    behavior,
    measurement,
  });
}

/**
 * Determines if a MetricEntry represents a saved behavior
 * @returns true if the entry has a behavior ID and is of type Funnel, Retention, or Simple
 */
export function isSavedBehavior(entry: MetricEntry) {
  return isNil(entry.behavior.id) === false && BEHAVIOR_METRIC_TYPES.includes(entry.behavior.type);
}

export function convertPropertySelectiontoBookmarkProperty(
  property: Property | PropertySectionItem,
): MetricMeasurementProperty {
  const {resourceType, value} = property;
  let customProperty,
    customPropertyId,
    dataGroupId,
    tempDataGroupId,
    joinPropertyType,
    propertyDefaultType,
    propertyObjectKey,
    scdEvent,
    scdOffset,
    type,
    unit,
    behavior,
    listItemGroup;

  if (isProperty(property)) {
    ({
      customProperty,
      customPropertyId,
      dataGroupId,
      tempDataGroupId,
      joinPropertyType,
      propertyDefaultType,
      propertyObjectKey,
      scdEvent,
      type,
      unit,
      listItemGroup,
      scdOffset,
      behavior,
    } = property);
  }
  return {
    ...(customProperty ? {customProperty} : {}),
    ...(customPropertyId ? {customPropertyId} : {}),
    ...(behavior ? {behavior} : {}),
    dataset: DATASET_MIXPANEL,
    ...(dataGroupId ? {dataGroupId} : {}),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : {}),
    ...(listItemGroup ? {listItemGroup} : {}),
    defaultType: propertyDefaultType,
    ...(propertyObjectKey ? {propertyObjectKey} : {}),
    ...(scdEvent ? {scdEvent} : {}),
    ...(scdOffset ? {scdOffset} : {}),
    name: value,
    resourceType: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[resourceType],
    type,
    unit: unit ?? null,
  };
}
